import clsx from "clsx";
import dayjs from "dayjs";
import { truncate } from "lodash";
import { useMemo, useState } from "react";

import { useBreakLG } from "@monta/design-system";
import { FaceLaugh, FacePokerface, FaceSad, Info } from "@monta/icons";
import { useSearchParams } from "@monta/router-react";
import styles from "./styles.module.scss";

import type { Props } from "./types";

const GeneralInfoHeader = (props: Readonly<Props>) => {
  const {
    level,
    name,
    rank,
    totalStudentNO,
    takingDate,
    tierNO,
    presentStudentNo
  } = props;

  const [searchParams] = useSearchParams();
  const isSimulator = searchParams.get("isSimulator");
  const breakLg = useBreakLG();
  const [showTooltip, setShowTooltip] = useState(false);

  const generateTithe = useMemo(() => {
    if ([1, 2, 3].includes(tierNO))
      return {
        icon: <FaceLaugh size={breakLg ? 40 : 20} />,
        text: "خــــــــــــــــــــوب",
        className: styles["tithe__text--green"]
      };

    if ([4, 5, 6].includes(tierNO))
      return {
        icon: <FacePokerface size={breakLg ? 40 : 20} />,
        text: "متـــــــــــــــــوسط",
        className: styles["tithe__text--blue"]
      };

    if ([7, 8, 9, 10].includes(tierNO))
      return {
        icon: <FaceSad size={breakLg ? 40 : 20} />,
        text: "ضعـیـــــــــــــــــف",
        className: styles["tithe__text--red"]
      };
  }, [breakLg, tierNO]);

  const showTithe = presentStudentNo && presentStudentNo >= 10;

  return (
    <section className={styles["header"]}>
      <div className={styles["header__title"]}>
        <div className={styles["header__title-name"]}>
          {`گزارش کارنامه آزمون ${name}`.length >= 45 && !breakLg ? (
            <>
              <span>
                {truncate(`گزارش کارنامه آزمون ${name}`, { length: 45 })}
              </span>
              <Info onClick={() => setShowTooltip((prev) => !prev)} size={20} />
              {showTooltip && (
                <span className={styles["header__title-tooltip"]}>
                  گزارش کارنامه آزمون {name}
                </span>
              )}
            </>
          ) : (
            `گزارش کارنامه آزمون ${name}`
          )}
        </div>
        <div className={styles["header__title-info"]}>
          {breakLg && !isSimulator ? (
            <span>
              {presentStudentNo} نفر از {totalStudentNO} نفر در آزمون حاضر بودند
            </span>
          ) : (
            <></>
          )}
          <span>
            {dayjs(takingDate).calendar("jalali").format("YYYY/MM/DD")}
          </span>
        </div>
      </div>
      <div
        className={clsx(
          styles["header__bottom-section"],
          !showTithe && styles["header__bottom-section--centered"]
        )}
      >
        <div className={styles["header__bottom-section__items"]}>
          <div className={styles["header__bottom-section__item"]}>
            <div className={styles["header__bottom-section__item__title"]}>
              رتبه
              {breakLg ? "" : ":"}
            </div>
            <div className={styles["header__bottom-section__item__subtitle"]}>
              {rank + " "}
              از
              {" " + totalStudentNO}
            </div>
          </div>
          <div className={styles["header__bottom-section__item"]}>
            <div className={styles["header__bottom-section__item__title"]}>
              تراز{breakLg ? "" : ":"}
            </div>
            <div className={styles["header__bottom-section__item__subtitle"]}>
              {level}
            </div>
          </div>
          {!breakLg && (
            <div className={styles["header__bottom-section__item"]}>
              <div className={styles["header__bottom-section__item__title"]}>
                حاضرین{breakLg ? "" : ":"}
              </div>
              <div className={styles["header__bottom-section__item__subtitle"]}>
                {presentStudentNo} نفر از {totalStudentNO} نفر
              </div>
            </div>
          )}
        </div>
        {showTithe && (
          <div className={clsx(styles["tithe"], generateTithe?.className)}>
            <div className={styles["tithe__text"]}>
              {generateTithe?.icon}
              <span className={styles["tithe__text--title"]}>
                جزو دهک {tierNO} هستید
              </span>
              <span className={styles["tithe__divider"]}>|</span>
              <span>{generateTithe?.text}</span>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default GeneralInfoHeader;
